import type { MutableRefObject } from 'react';
import { Crepe } from '@milkdown/crepe';
import { renderToStaticMarkup } from 'react-dom/server';
import { Link } from '@phosphor-icons/react';
import { oneDark } from '@codemirror/theme-one-dark';
import { vitesseLight } from 'codemirror-theme-vitesse';
import type { EditorView } from '@milkdown/kit/prose/view';

import { dateTimeSlashMenuItem, DateType } from '../../plugins/dateTimeSlashItem';
import { SlashMenuItems } from '../../nodes/notice-block';
import { shouldShowVariableContentSlashItem, VariableContentSlashMenuItems } from '../../nodes/variable-content';
import { toggleLinkMark } from '../../plugins/link-tooltip';
import { configureGrammarToolbarButton } from '../../plugins/grammarCheck';
import { configureEmbedSlashMenu } from '../../nodes/embed';
import { configureVideoSlashMenu } from '../../nodes/video';
import { configureAttachmentSlashMenu } from '../../nodes/attachment';
import type { BrainfishEditorProps, MediaUploadHandler } from '../../types';
import fallbackImageSrc from '../../assets/image-error.svg';
import { addFormattingToolbarItems } from '../formattingToolbar';

/**
 * Default image upload handler - creates a blob URL (not persisted).
 * Used when no onUploadImage callback is provided.
 */
const defaultImageUpload: MediaUploadHandler = (file) => Promise.resolve(URL.createObjectURL(file));

interface BuildCrepeOpts {
  root: HTMLElement;
  defaultValue: string | undefined;
  resolvedThemeRef: MutableRefObject<'light' | 'dark'>;
  onUploadImage?: MediaUploadHandler;
  onGrammarCheckRef: MutableRefObject<((text: string) => Promise<string>) | undefined>;
  imageErrorFallbackUrlRef: MutableRefObject<string | undefined>;
  /** Live channel list — slash items for variable content only when non-empty. */
  channelsRef?: MutableRefObject<NonNullable<BrainfishEditorProps['channels']>>;
  /** Current ProseMirror view — used to hide Variable content slash when nested. */
  editorViewRef?: MutableRefObject<EditorView | null>;
}

/**
 * Constructs the Crepe instance with all built-in feature configs:
 * BlockEdit (slash menu), Toolbar, ImageBlock, CodeMirror.
 */
export function buildCrepe({
  root,
  defaultValue,
  resolvedThemeRef,
  onUploadImage,
  onGrammarCheckRef,
  imageErrorFallbackUrlRef,
  channelsRef,
  editorViewRef,
}: BuildCrepeOpts): Crepe {
  return new Crepe({
    root,
    defaultValue,
    features: {
      [Crepe.Feature.LinkTooltip]: false,
      [Crepe.Feature.Latex]: false,
    },
    featureConfigs: {
      // Note: We keep the default toolbar with the link button.
      // The link button triggers the built-in toggleLinkCommand which
      // our custom link tooltip will respond to.
      [Crepe.Feature.BlockEdit]: {
        buildMenu: (builder) => {
          // Add callouts group with notice types
          const calloutsGroup = builder.addGroup('callouts', 'Callouts');
          calloutsGroup
            .addItem('info', SlashMenuItems.info())
            .addItem('warning', SlashMenuItems.warning())
            .addItem('tip', SlashMenuItems.tip());

          // Add embeds group (YouTube, Vimeo, Loom, etc.)
          const embedsGroup = builder.addGroup('embeds', 'Embeds');
          configureEmbedSlashMenu(embedsGroup);

          // Add media group (video upload, file attachment)
          const advancedGroup = builder.getGroup('advanced');
          configureVideoSlashMenu(advancedGroup);
          configureAttachmentSlashMenu(advancedGroup);
          if ((channelsRef?.current.length ?? 0) > 0) {
            if (shouldShowVariableContentSlashItem(editorViewRef?.current)) {
              advancedGroup.addItem('variable_content', VariableContentSlashMenuItems.variableContent());
            }
          }

          // Add utilities group with date/time commands
          const utilitiesGroup = builder.addGroup('utilities', 'Utilities');
          utilitiesGroup
            .addItem('date_time', dateTimeSlashMenuItem({ type: DateType.DATE_TIME }))
            .addItem('date', dateTimeSlashMenuItem({ type: DateType.DATE }))
            .addItem('time', dateTimeSlashMenuItem({ type: DateType.TIME }));
        },
      },
      [Crepe.Feature.Toolbar]: {
        buildToolbar: (builder) => {
          // Block formatting: headings and lists (same group as Bold, Italic, Strikethrough)
          const formattingGroup = builder.getGroup('formatting');
          addFormattingToolbarItems(formattingGroup);

          // Configure link button with custom toggle
          const functionGroup = builder.getGroup('function');
          const linkButton = functionGroup.group.items.find((el) => el.key === 'link');
          if (linkButton) {
            linkButton.onRun = toggleLinkMark;
            linkButton.icon = renderToStaticMarkup(<Link size={24} aria-label="Add link" />);
          }

          // Add grammar check button
          configureGrammarToolbarButton(functionGroup, onGrammarCheckRef);
        },
      },
      // ImageBlock: configure upload handler for S3
      [Crepe.Feature.ImageBlock]: {
        onUpload: onUploadImage ?? defaultImageUpload,
        onImageLoadError: (event: Event) => {
          // set the target if imageErrorFallbackUrl is provided
          const target = event.target as HTMLImageElement;
          if (imageErrorFallbackUrlRef.current) {
            target.src = imageErrorFallbackUrlRef.current;
          } else {
            target.src = fallbackImageSrc;
          }
        },
      },
      [Crepe.Feature.CodeMirror]: {
        theme: resolvedThemeRef.current === 'dark' ? oneDark : vitesseLight,
      },
    },
  });
}
